import React, { FC, useCallback, useContext, useMemo } from 'react';
import { has } from 'lodash';

import type {
  IChangeableAndSubmittable,
  ILegoElem,
  ILegoElemComponentMap,
  ILegoElemLego,
  ILegoElemObject,
  ILegoElemsContainer,
  ILegoElemSelect,
} from './_Elem/models';
import { LegoFormProvider } from './_FormProvider/LegoFormProvider';
import { IBoxWrapped, IClassNameProps, ILinkComponent, IStylesProps } from '@src/models/props';
import { AnyType } from '@src/models/common';

import { LegoElemButton } from './_Elem/_Button/LegoElemButton';
import { LegoElemCheckbox } from './_Elem/_Checkbox/LegoElemCheckbox';
import { LegoElemDatePicker } from './_Elem/_DatePicker/LegoElemDatePicker';
import { LegoElemInput } from './_Elem/_Input/LegoElemInput';
import { LegoElemInputPassword } from './_Elem/_InputPassword/LegoElemInput';
import { LegoElemMediaUploader } from './_Elem/_MediaUploader/LegoElemMediaUploader';
import { LegoElemRichTextEditor } from './_Elem/_RichTextEditor/LegoElemRichTextEditor';
import { LegoElemSelect } from './_Elem/_Select/LegoElemSelect';
import { LegoElemSwitch } from './_Elem/_Switch/LegoElemSwitch';
import { LegoElemTag } from './_Elem/_Tag/LegoElemTag';
import { LegoElemText } from './_Elem/_Text/LegoElemText';
import { LegoElemCustomInput } from './_Elem/_CustomInput/LegoElemCustomInput';
import { LegoElemTextarea } from './_Elem/_Textarea/LegoElemTextarea';
import { useLegoFormContext } from './_FormProvider/useLegoFormContext';
import { Box } from '@src/atoms/Box';
import { Form } from '@src/atoms/Form';
import { InputLabel } from '@src/atoms/InputLabel';
import { classnames } from '@src/utils/classnames';
import { BreakpointsContext } from '@src/providers/Breakpoints';

import styles from './Lego.scss';
import { LegoElemInputPhone } from '@src/molecules/Lego/_Elem/_InputPhone/LegoElemInputPhone';
import { useLegoElemDependsOptionFilters } from '@src/hooks/useLegoElemDependsOptionFilters';
import { IOptionFilter } from '@src/models/api';
import { reBuildElems } from '@src/molecules/Lego/utils';
import { LegoElemReactNode } from '@src/molecules/Lego/_Elem/_ReactNode/LegoElemReactNode';
import { LegoElemRadioButton } from './_Elem/_RadioButton/LegoElemRadioButton';
import { LegoElemUploader } from './_Elem/_Uploader/LegoElemUploader';

export interface ILegoContentProps extends IClassNameProps, IStylesProps, ILegoElemsContainer, IBoxWrapped {}

/**
 * Чистый рендеринг элементов без понимания в Form оно или нет
 */
export const LegoContent: FC<ILegoContentProps> = ({ className, style, elems, boxWrap }) => {
  const classes = classnames(className, styles.legoContent, {});
  const loading = false;
  const { isMobile } = useContext(BreakpointsContext);
  const { formHook } = useLegoFormContext();

  const preparedElems = useMemo(() => {
    return reBuildElems(elems, formHook.getValues());
  }, [elems, formHook]);

  return (
    <Box
      className={classes}
      style={style}
      /**
       * Добавим расстояние для корректного отображения ошибок и лейбла у обновленного инпута в верхнем состоянии
       * https://centrateam.atlassian.net/browse/CA-2995?focusedCommentId=13611
       */
      gap={8}
      {...boxWrap}
    >
      {preparedElems?.map((elem) => {
        return <LegoElem key={elem.id} elem={elem} loading={loading} isMobile={isMobile} />;
      })}
    </Box>
  );
};

export interface ILegoProps
  extends IClassNameProps,
    IStylesProps,
    ILinkComponent,
    ILegoElemsContainer,
    IBoxWrapped,
    IChangeableAndSubmittable {
  value?: AnyType;
  /**
   * Открывает новую форму и начинает перехватывать onSubmit и пр.
   * По сути isForm и всегда нужен для верхнеуровневых лего.
   * Если false, то и submit-а не будет, это надо для суб-форм в рамках формы.
   */
  isNewForm?: boolean;
}

export const Lego: FC<ILegoProps> = (props) => {
  if (props.isNewForm) {
    // Лего открывает форму
    return (
      <LegoFormProvider value={props.value} elems={props.elems} onSubmit={props.onSubmit} onChange={props.onChange}>
        <LegoContent {...props} />
      </LegoFormProvider>
    );
  }
  return <LegoContent {...props} />;
};

export const LegoElemLego: FC<ILegoElemProps> = ({ elem }) => {
  const legoElem = elem as ILegoElemLego;
  const { formHook } = useLegoFormContext();
  const value = formHook.watch(elem.id);

  const handleOnChange = useCallback(
    (formData: AnyType) => {
      // Если это субформа, то мы просто записываем весь ее контент на уровень текущей формы
      // todo. Наверно лучше делать не так, а через точку - с более нижних слоев зная, где находишься
      //  чтобы не обновлять все дерево
      formHook.setValueAndRevalidate(elem.id, formData);
    },
    [elem.id, formHook],
  );

  return <Lego value={value} onChange={handleOnChange} {...legoElem.lego} />;
};

export const LEGO_ELEM_COMPONENT_MAP: ILegoElemComponentMap = {
  reactNode: {
    component: LegoElemReactNode,
    disableFormItem: true,
  },
  customInput: {
    component: LegoElemCustomInput,
    disableFormItem: true,
  },
  input: {
    component: LegoElemInput,
    disableFormItem: true,
  },
  inputPassword: {
    component: LegoElemInputPassword,
    disableFormItem: true,
  },
  inputPhone: {
    component: LegoElemInputPhone,
    disableFormItem: true,
  },
  checkbox: {
    component: LegoElemCheckbox,
  },
  switch: {
    component: LegoElemSwitch,
  },
  select: {
    component: LegoElemSelect,
    disableFormItem: true,
  },
  textarea: {
    component: LegoElemTextarea,
    disableFormItem: true,
  },
  richTextEditor: {
    component: LegoElemRichTextEditor,
  },
  button: {
    component: LegoElemButton,
    disableFormItem: true,
  },
  text: {
    component: LegoElemText,
    disableFormItem: true,
  },
  tag: {
    disableFormItem: true,
    component: LegoElemTag,
  },
  lego: {
    component: LegoElemLego,
    disableFormItem: true,
  },
  mediaUploader: {
    component: LegoElemMediaUploader,
  },
  uploader: {
    component: LegoElemUploader,
  },
  datePicker: {
    component: LegoElemDatePicker,
    disableFormItem: true,
  },
  radioButton: {
    component: LegoElemRadioButton,
    disableFormItem: true,
  },
};

export const ALLOWED_ELEM_TYPES = Object.keys(LEGO_ELEM_COMPONENT_MAP);

export interface ILegoElemProps {
  elem: ILegoElem;
  loading: boolean;
  isMobile: boolean;
  dependsEntries?: IOptionFilter[];
  errorMessage?: string;
}

export const LegoElem: FC<ILegoElemProps> = (props) => {
  const { formHook } = useLegoFormContext();
  const { elem } = props;
  const legoElem = elem as ILegoElem;
  // К any приходится приводить, так как иначе начинает ругаться, что
  // в textarea нет .input и пр.
  const typedElem = legoElem as AnyType;

  const dependsEntries = useLegoElemDependsOptionFilters(legoElem, () => {
    if (dependsEntries.length > 0) {
      const currValue = formHook.getValues(legoElem.id);
      if (currValue !== undefined && currValue !== null) {
        // Если элемент зависит от того, который поменялся
        // надо очистить value у зависимого
        formHook.setValueAndRevalidate(legoElem.id, undefined);

        // Продолжение костыля для Select и добавление в данных лейблов для опшенов
        const addLabelIntoValueForSelect = (legoElem as ILegoElemSelect)?.select?.addLabelIntoValue;
        if (addLabelIntoValueForSelect) {
          formHook.setValueAndRevalidate(addLabelIntoValueForSelect, undefined);
        }
      }
    }
  });

  // Ищем компонент, который нужно зарендерить
  const { component: Component, disableFormItem } = useMemo<ILegoElemObject>(() => {
    for (const elemType of ALLOWED_ELEM_TYPES) {
      if (has(typedElem, elemType)) {
        return LEGO_ELEM_COMPONENT_MAP[elemType];
      }
    }

    return { component: () => <b>Undefined elem</b>, disableFormItem: true };
  }, [typedElem]);

  const label = useMemo(() => {
    if (elem.label) {
      const newLabel = { ...elem.label };
      if (newLabel.required === undefined && elem.validators?.required) {
        newLabel.required = true;
      }
      return newLabel;
    }
    return undefined;
  }, [elem]);

  if (!formHook || !formHook.watch) {
    return <b>LegoForm на настроена должным образом - не хватет formHook. Передайте обработчики для onSubmit</b>;
  }

  return (
    <Box key={elem.id} {...elem.boxWrap}>
      {disableFormItem ? (
        <>
          <Box column fullWidth>
            {label && <InputLabel {...label} />}
            <Component
              {...props}
              dependsEntries={dependsEntries}
              //@ts-ignore
              errorMessage={formHook.errors[elem.id]?.message}
            />
          </Box>
        </>
      ) : (
        <Form.Item error={formHook.errors[elem.id]?.message}>
          {label && <InputLabel {...label} />}
          <Component {...props} dependsEntries={dependsEntries} />
        </Form.Item>
      )}
    </Box>
  );
};
